import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, query } from './api';
import type {
  Absence,
  AssignmentResult,
  AuditEntry,
  BalanceResponse,
  Crew,
  Employee,
  Holiday,
  OvertimeEntry,
  PlanData,
  RangeAssignResult,
  SessionUser,
  Site,
  SiteDay,
  SiteDayResult,
} from './types';
import type { DateRange } from './dates';

export const keys = {
  session: ['session'] as const,
  crews: ['crews'] as const,
  employees: ['employees'] as const,
  sites: ['sites'] as const,
  users: ['users'] as const,
  audit: ['audit'] as const,
  plan: (range: DateRange) => ['plan', range.from, range.to] as const,
  balances: (year: number) => ['balances', year] as const,
  absences: (employeeId?: number) => ['absences', employeeId ?? 'alle'] as const,
  absencesRange: (range: DateRange) => ['absences-range', range.from, range.to] as const,
  overtime: (employeeId?: number) => ['overtime', employeeId ?? 'alle'] as const,
  holidays: (year: number) => ['holidays', year] as const,
};

/** Themen der Live-Meldungen auf die betroffenen Abfragen abbilden. */
const TOPIC_INVALIDATION: Record<string, readonly unknown[][]> = {
  crews: [[...keys.crews], ['plan']],
  employees: [[...keys.employees], ['plan'], ['balances']],
  sites: [[...keys.sites], ['plan']],
  'site-days': [['plan']],
  assignments: [['plan']],
  absences: [['plan'], ['absences'], ['absences-range'], ['balances']],
  overtime: [['overtime'], ['balances']],
};

export function invalidateForTopic(client: QueryClient, topic: string): void {
  for (const key of TOPIC_INVALIDATION[topic] ?? [['plan']]) {
    void client.invalidateQueries({ queryKey: key });
  }
}

// --- Sitzung ---

export function useSession() {
  return useQuery({
    queryKey: keys.session,
    queryFn: () => api.get<{ user: SessionUser | null }>('/auth/me'),
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { username: string; password: string }) =>
      api.post<{ user: SessionUser }>('/auth/login', input),
    onSuccess: (data) => {
      client.setQueryData(keys.session, { user: data.user });
      void client.invalidateQueries();
    },
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      client.setQueryData(keys.session, { user: null });
      client.clear();
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post('/auth/password', input),
  });
}

// --- Stammdaten ---

export function useCrews() {
  return useQuery({ queryKey: keys.crews, queryFn: () => api.get<Crew[]>('/crews') });
}

export function useEmployees() {
  return useQuery({ queryKey: keys.employees, queryFn: () => api.get<Employee[]>('/employees') });
}

export function useSites() {
  return useQuery({ queryKey: keys.sites, queryFn: () => api.get<Site[]>('/sites') });
}

/** Erzeugt Anlegen/Ändern/Löschen für eine Sammlung in einem Rutsch. */
function collectionMutations<TInput>(resource: string, invalidate: readonly unknown[][]) {
  return {
    useCreate() {
      const client = useQueryClient();
      return useMutation({
        mutationFn: (input: TInput) => api.post(`/${resource}`, input),
        onSuccess: () => invalidate.forEach((key) => void client.invalidateQueries({ queryKey: key })),
      });
    },
    useUpdate() {
      const client = useQueryClient();
      return useMutation({
        mutationFn: ({ id, ...input }: TInput & { id: number }) => api.put(`/${resource}/${id}`, input),
        onSuccess: () => invalidate.forEach((key) => void client.invalidateQueries({ queryKey: key })),
      });
    },
    useDelete() {
      const client = useQueryClient();
      return useMutation({
        mutationFn: ({ id, purge }: { id: number; purge?: boolean }) =>
          api.delete(`/${resource}/${id}${purge ? '?purge=1' : ''}`),
        onSuccess: () => invalidate.forEach((key) => void client.invalidateQueries({ queryKey: key })),
      });
    },
  };
}

export const crewMutations = collectionMutations<Omit<Crew, 'id'>>('crews', [
  [...keys.crews],
  ['plan'],
  [...keys.employees],
]);

export const employeeMutations = collectionMutations<Omit<Employee, 'id' | 'displayName'>>(
  'employees',
  [[...keys.employees], ['plan'], ['balances']],
);

export const siteMutations = collectionMutations<Omit<Site, 'id'>>('sites', [
  [...keys.sites],
  ['plan'],
]);

// --- Plan ---

export function usePlan(range: DateRange) {
  return useQuery({
    queryKey: keys.plan(range),
    queryFn: () => api.get<PlanData>(`/plan${query({ from: range.from, to: range.to })}`),
    placeholderData: (previous) => previous,
  });
}

/** Baustelle für einen Tag (oder bis zu einem Tag) in den Plan nehmen. */
export function useAddSiteDay() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      siteId: number;
      date: string;
      until?: string;
      includeWeekends?: boolean;
    }) => api.post<SiteDayResult>('/site-days', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['plan'] }),
  });
}

/** Tages-Info einer Baustelle setzen oder leeren — gilt nur für genau diesen Tag. */
export function useSetSiteDayNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { siteId: number; date: string; note: string }) =>
      api.put<SiteDay>(`/site-days/${input.siteId}/${input.date}/note`, { note: input.note }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['plan'] }),
  });
}

/** Baustelle von einem Tag nehmen — die Einteilungen des Tages gehen mit. */
export function useRemoveSiteDay() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { siteId: number; date: string }) =>
      api.delete<{ removedAssignments: number }>(`/site-days/${input.siteId}/${input.date}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['plan'] }),
  });
}

export function useAssignSite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { employeeId: number; siteId: number; date: string; note?: string }) =>
      api.post<AssignmentResult>('/assignments', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['plan'] }),
  });
}

export function useRemoveAssignment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/assignments/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['plan'] }),
  });
}

export function useClearDay() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { employeeId: number; date: string }) =>
      api.delete<{ removed: number }>(`/assignments/cell/${input.employeeId}/${input.date}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['plan'] }),
  });
}

export function useAssignRange() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      employeeIds: number[];
      siteId: number;
      from: string;
      to: string;
      includeWeekends: boolean;
      skipAbsent: boolean;
      replace: boolean;
      note?: string;
    }) => api.post<RangeAssignResult>('/assignments/range', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['plan'] }),
  });
}

// --- Verfügbarkeiten ---

export function useAbsences(employeeId?: number) {
  return useQuery({
    queryKey: keys.absences(employeeId),
    queryFn: () => api.get<Absence[]>(`/absences${query({ employeeId })}`),
    enabled: employeeId !== undefined,
  });
}

export function useAbsencesInRange(range: DateRange) {
  return useQuery({
    queryKey: keys.absencesRange(range),
    queryFn: () => api.get<Absence[]>(`/absences${query({ from: range.from, to: range.to })}`),
    placeholderData: (previous) => previous,
  });
}

const ABSENCE_KEYS = [['absences'], ['absences-range'], ['plan'], ['balances']];

export const absenceMutations = collectionMutations<Omit<Absence, 'id' | 'createdBy' | 'updatedAt'>>(
  'absences',
  ABSENCE_KEYS,
);

export function useOvertime(employeeId?: number) {
  return useQuery({
    queryKey: keys.overtime(employeeId),
    queryFn: () => api.get<OvertimeEntry[]>(`/overtime${query({ employeeId })}`),
  });
}

export function useAddOvertime() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { employeeId: number; date: string; hours: number; reason: string }) =>
      api.post<OvertimeEntry>('/overtime', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['overtime'] });
      void client.invalidateQueries({ queryKey: ['balances'] });
    },
  });
}

export function useDeleteOvertime() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/overtime/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['overtime'] });
      void client.invalidateQueries({ queryKey: ['balances'] });
    },
  });
}

export function useBalances(year: number) {
  return useQuery({
    queryKey: keys.balances(year),
    queryFn: () => api.get<BalanceResponse>(`/plan/balances${query({ year })}`),
  });
}

export function useHolidays(year: number) {
  return useQuery({
    queryKey: keys.holidays(year),
    queryFn: () => api.get<Holiday[]>(`/holidays${query({ year })}`),
    staleTime: Infinity,
  });
}

// --- Verwaltung ---

export function useUsers(enabled: boolean) {
  return useQuery({
    queryKey: keys.users,
    queryFn: () => api.get<SessionUser[]>('/users'),
    enabled,
  });
}

export const userMutations = collectionMutations<Record<string, unknown>>('users', [[...keys.users]]);

export function useAuditLog(enabled: boolean) {
  return useQuery({
    queryKey: keys.audit,
    queryFn: () => api.get<AuditEntry[]>('/audit?limit=150'),
    enabled,
  });
}

export function useBackup() {
  return useMutation({ mutationFn: () => api.post<{ file: string }>('/backup') });
}
